import { CaretUpDownIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useTableDataQuery } from '@/modules/common';
import { PaginatedTable } from '@/modules/common/components';

import { useGcrFormOptions } from '../api';
import { GcrDetails } from '../types';
import { TableFilters } from './table-filters';

interface GcrsTableProps {
  onClick: (item: GcrDetails) => void;
}

const uniqueValues = (values: string[]) => [...new Set(values)].join(', ');

export function GcrsTable({ onClick }: GcrsTableProps) {
  const { optionsData, isFetchingPrinters } = useGcrFormOptions();

  const { data, isFetching, refetch, filters, setFilters, ...tableProps } =
    useTableDataQuery<GcrDetails>({
      url: '/gcrs',
      persistState: true,
    });

  useEffect(() => {
    if (tableProps.isError) {
      toast.error(tableProps.error?.message || 'Uh oh! Something went wrong.');
    }
  }, [tableProps.isError, tableProps.error]);

  const columns: ColumnDef<GcrDetails>[] = [
    {
      accessorKey: 'name',
      header: 'File Upload',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('name')}</span>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Model Type',
      cell: ({ row }) => (
        <span className="line-clamp-1">
          {uniqueValues(row.original.combinations.map((c) => c.type.value))}
        </span>
      ),
    },
    {
      accessorKey: 'size',
      header: 'Size',
      cell: ({ row }) => (
        <span className="line-clamp-1">
          {uniqueValues(
            [...row.original.combinations]
              .sort((a, b) => a.size.sortOrder - b.size.sortOrder)
              .map((c) => c.size.value)
          )}
        </span>
      ),
    },
    {
      accessorKey: 'color',
      header: 'Color',
      cell: ({ row }) => (
        <span className="line-clamp-1">
          {row.original.combinations[0]?.color.value ?? ''}
        </span>
      ),
    },
    {
      accessorKey: 'printer',
      header: 'Printer Name',
      cell: ({ row }) => (
        <span className="line-clamp-1">
          {row.original.combinations[0]?.printer.value ?? ''}
        </span>
      ),
    },
    {
      accessorKey: 'updatedAt',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Uploaded Date
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) =>
        format(new Date(row.getValue('updatedAt')), 'yyyy-MM-dd'),
    },
  ];

  return (
    <>
      <TableFilters
        options={optionsData}
        filters={filters}
        setFilters={setFilters}
      />
      <PaginatedTable<GcrDetails>
        data={data}
        onClick={onClick}
        columns={columns}
        emptyDescription="No gcr files uploaded yet"
        isLoading={isFetching || isFetchingPrinters}
        {...tableProps}
      />
    </>
  );
}
